import { Entity } from '@/stores/slices/ApiSlice';
import { useStore } from '@/stores/store';
import { useState } from 'react';
import useDelete, { DeleteOptions } from './useDelete';
import useGet, { GetOptions } from './useGet';
import usePatch, { PatchOptions } from './usePatch';
import usePost, { PostOptions } from './usePost';
import usePut, { PutOptions } from './usePut';

export type Keys =
  | 'users'
  | 'assistants'
  | 'auth'
  | 'createPassword'
  | 'resetPassword'
  | 'me'
  | 'organizations'
  | 'knowledge'
  | 'missions'
  | 'notificationToken'
  | 'activities'
  | 'knowledgeSuggestions'
  | 'tags'
  | 'thread'
  | 'threadAttendQueue'
  | 'outboundThreads'
  | 'contactFile'
  | 'channels'
  | 'testNotification'
  | 'suggestions'
  | 'integrations'
  | 'preSubscriptions'
  | 'subscriptions'
  | 'threadUser'
  | 'affiliate'
  | 'userOrganizations'
  | 'helpCenterKnowledge'
  | 'embeddedKnowledge'
  | 'helpCenterEmbeddedKnowledge'
  | 'attendanceQueue'
  | 'diagnostics'
  | 'funnels'
  | 'funnelStage'
  | 'funnelStages'
  | 'createWhatsAppMessage'
  | 'files'
  | 'filePresignedUrl'
  | 'report';

export const ApiConstants = {
  users: {
    url: '/api/user',
    entity: 'users',
  },
  assistants: {
    url: '/api/assistant',
    entity: 'assistants',
  },
  organizations: {
    url: '/api/organization',
    entity: 'organizations',
  },
  helpCenterKnowledge: {
    url: '/api/organization/organization',
    entity: 'helpCenterKnowledge',
  },
  knowledge: {
    url: '/api/knowledge',
    entity: 'knowledge',
  },
  missions: {
    url: '/api/mission',
    entity: 'missions',
  },
  activities: {
    url: '/api/activity',
    entity: 'activities',
  },
  knowledgeSuggestions: {
    url: '/api/knowledgeSuggestion',
    entity: 'knowledgeSuggestions',
  },
  embeddedKnowledge: {
    url: '/api/knowledge/embedded/',
    entity: 'embeddedKnowledge',
  },
  helpCenterEmbeddedKnowledge: {
    url: '/api/knowledge/embedded/',
    entity: 'helpCenterEmbeddedKnowledge',
  },
  outboundThreads: {
    url: '/api/mission',
    entity: 'outboundThreads',
  },
  tags: {
    url: '/api/tag',
    entity: 'tags',
  },
  thread: {
    url: '/api/thread',
    entity: 'thread',
  },
  attendanceQueue: {
    url: '/api/attendanceQueue',
    entity: 'attendanceQueue',
  },
  channels: {
    url: '/api/channel',
    entity: 'channels',
  },
  affiliate: {
    url: '/api/affiliate',
    entity: 'affiliates',
  },
  funnels: {
    url: '/api/funnel',
    entity: 'funnels',
  },
  funnelStage: {
    url: '/api/funnelStage',
    entity: null, // Not stored in global state
  },
  funnelStages: {
    url: '/api/funnelStage/stages',
    entity: null, // Not stored in global state
  },
  auth: {
    url: '/api/auth/login',
  },
  createPassword: {
    url: '/api/auth/createPassword',
  },
  me: {
    url: '/api/auth/me',
  },
  resetPassword: {
    url: '/api/auth/resetPassword',
  },
  notificationToken: {
    url: '/api/auth/me/notificationToken',
  },
  threadAttendQueue: {
    url: '/api/thread',
  },
  contactFile: {
    url: '/api/file',
  },
  testNotification: {
    url: '/api/system/testNotification',
  },
  integrations: {
    url: '/api/integration',
  },
  suggestions: {
    url: '/api/mission',
  },
  preSubscriptions: {
    url: '/api/preSubscription',
  },
  subscriptions: {
    url: '/api/subscription',
  },
  threadUser: {
    url: '/api/threadUser',
  },
  userOrganizations: {
    url: '/api/userOrganization',
  },
  diagnostics: {
    url: '/api/diagnostics',
  },
  createWhatsAppMessage: {
    url: '/api/webhook/createWhatsAppMessage',
  },
  files: {
    url: '/api/file/files',
    entity: 'files',
  },
  filePresignedUrl: {
    url: '/api/file/files/presigned-url',
  },
  report: {
    url: '/api/report',
    entity: null,
  },
} as {
  [key in Keys]: {
    url: string;
    entity?: Entity | null;
  };
};

export type ErrorType = { status?: number; message: string; data?: unknown };

function useApi<T>(key: Keys) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ErrorType>();

  const { get } = useGet(key, { autoFetch: false } as unknown as GetOptions<T>);
  const { post } = usePost<T>(key);
  const { put } = usePut<T>(key);
  const { patch } = usePatch<T>(key);
  const { remove } = useDelete(key);

  if (!ApiConstants[key]) throw new Error(`Key ${key} not found in ApiConstants`);

  const entity = ApiConstants[key].entity;
  const data = useStore((store) => (entity ? store[entity] : null)) as T[] | null;

  return {
    data,
    loading,
    error,
    useGet: (options: Omit<GetOptions<T>, 'autoFetch'>) =>
      useGet<T>(key, { ...options, autoFetch: true }, setLoading, setError),
    get: function <K>(options: Omit<GetOptions<K>, 'autoFetch'>) {
      return get<K>(options, setLoading, setError);
    },
    post: function <K>(data: T, options: PostOptions<K>) {
      return post<K>(data, options, setLoading, setError);
    },
    put: function <K>(data: T, options: PutOptions<K>, id?: string) {
      return put<K>(data, options, setLoading, setError, id);
    },
    patch: function <K>(data: T, options: PatchOptions<K>, id?: string) {
      return patch<K>(data, options, setLoading, setError, id);
    },
    remove: (id: string, options: DeleteOptions) => remove(id, options, setLoading, setError),
    setLoading,
    setError,
  };
}

export default useApi;
